import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "py-2 space-y-4" }
const _hoisted_2 = { class: "flex space-between gap-2 sm:gap-4" }
const _hoisted_3 = { class: "sm:flex sm:items-center gap-2 sm:gap-4 sm:w-full text-left w-fit" }
const _hoisted_4 = { class: "flex items-center gap-2 sm:gap-4 mt-4 sm:mt-0" }
const _hoisted_5 = { class: "sm:relative absolute sm:right-0 right-2" }
const _hoisted_6 = ["src"]

import { Button, Select } from 'ant-design-vue';
import { DefaultOptionType } from 'ant-design-vue/es/select';
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
	CaretLeftOutlined,
	CaretRightOutlined,
	HomeOutlined,
	StepBackwardOutlined,
	StepForwardOutlined
} from '@ant-design/icons-vue';
import ImageCard from '@/components/shared/ImageCard.vue';
import RouterLink from '@/components/shared/RouterLink.vue';
import { DocumentType } from '@/model/document';
import { DocumentViewMode } from '@/model/universal';
import { getDocumentPagination } from '@/repository/document';

const imagePlaceholder =
	'https://www.signfix.com.au/wp-content/uploads/2017/09/placeholder-600x400.png';

export default /*#__PURE__*/_defineComponent({
  __name: 'HitomiReaderView',
  setup(__props) {

const router = useRouter();
const { params, hash } = useRoute();
const { push } = useRouter();

const code = params.code as string;
const hitomiUUID = params.hitomiUUID as string;
const current = ref(hash ? Math.floor(Number(hash.substring(1))) : 1);
const images = ref<
	{
		url: string;
		isLoading: boolean;
		isError: boolean;
	}[]
>([]);
const stacks = ref<number[]>([]);
const isLoading = ref(true);

const pageSelects = ref<DefaultOptionType[]>(selectOptions(current.value));
function selectOptions(size: number) {
	return [...Array(size)].map((_, index) => {
		const seq = `${index + 1}`;
		return {
			value: index + 1,
			title: seq,
			label: `Page ${seq}`,
			key: seq
		};
	});
}

onMounted(fetchDocument);
async function fetchDocument() {
	try {
		if (hash.length <= 1) {
			throw Error('invalid arguments');
		}
		if (isNaN(current.value) || current.value < 1) {
			current.value = 1;
			router.replace({ hash: `#${current.value}` });
		}

		const res = await getDocumentPagination({
			documentType: DocumentType.Hitomi,
			limit: 1000,
			metadataID: code,
			sort: 'created_at asc'
		});
		if (!res || !res.data.length) {
			throw Error('invalid documents');
		}
		stacks.value = res.data.map((_, index, arr) =>
			arr.slice(0, index).reduce((prev, cur) => prev + cur.files.length, 1)
		);
		images.value = res.data.flatMap((document) =>
			document.files.map((file) => ({
				url: file.originalURL,
				isLoading: true,
				isError: false
			}))
		);

		if (current.value > images.value.length) {
			current.value = images.value.length;
			router.replace({ hash: `#${current.value}` });
		}

		pageSelects.value = selectOptions(images.value.length);
	} catch (error) {
		push('/hitomi');
	} finally {
		isLoading.value = false;
	}
}

function changePage(event: 'PREVIOUS_SCENE' | 'PREVIOUS' | 'NEXT' | 'NEXT_SCENE') {
	switch (event) {
		case 'PREVIOUS_SCENE': {
			const curIndex = stacks.value.findIndex((seq) => seq >= current.value);
			if (curIndex > 0) {
				current.value = stacks.value[curIndex - 1];
			}
			break;
		}

		case 'PREVIOUS':
			if (current.value > 1) {
				current.value--;
			}
			break;

		case 'NEXT':
			if (current.value < images.value.length) {
				current.value++;
			}
			break;

		case 'NEXT_SCENE': {
			const nextIndex = stacks.value.find((seq) => seq > current.value) ?? -1;
			if (nextIndex !== -1) {
				current.value = nextIndex;
			}
			break;
		}
	}
}

watch(
	() => current.value,
	() => {
		router.replace({ hash: `#${current.value}` });
		images.value[current.value - 1].isLoading = true;
		images.value[current.value - 1].isError = false;
	}
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(RouterLink, {
          to: `/hitomi/${_unref(hitomiUUID)}`
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(Button), {
              type: "primary",
              class: "bg-white text-black"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(HomeOutlined))
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["to"]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_unref(Button), {
            onClick: _cache[0] || (_cache[0] = ($event: any) => (changePage('PREVIOUS_SCENE')))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(StepBackwardOutlined))
            ]),
            _: 1
          }),
          _createVNode(_unref(Button), {
            onClick: _cache[1] || (_cache[1] = ($event: any) => (changePage('PREVIOUS')))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(CaretLeftOutlined))
            ]),
            _: 1
          }),
          _createVNode(_unref(Button), {
            onClick: _cache[2] || (_cache[2] = ($event: any) => (changePage('NEXT')))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(CaretRightOutlined))
            ]),
            _: 1
          }),
          _createVNode(_unref(Button), {
            onClick: _cache[3] || (_cache[3] = ($event: any) => (changePage('NEXT_SCENE')))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(StepForwardOutlined))
            ]),
            _: 1
          })
        ])
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_unref(Select), {
          value: current.value,
          options: pageSelects.value,
          placeholder: `Please select page`,
          class: "text-start",
          onChange: _cache[4] || (_cache[4] = (value) => (current.value = Number(value)))
        }, null, 8, ["value", "options"])
      ])
    ]),
    _createVNode(ImageCard, {
      viewMode: _unref(DocumentViewMode).List,
      isLoading: isLoading.value || images.value[current.value - 1].isLoading
    }, null, 8, ["viewMode", "isLoading"]),
    (!isLoading.value)
      ? (_openBlock(), _createElementBlock("img", {
          key: 0,
          alt: "",
          src: images.value[current.value - 1].isError ? imagePlaceholder : images.value[current.value - 1].url,
          onLoad: _cache[5] || (_cache[5] = () => (images.value[current.value - 1].isLoading = false)),
          onError: _cache[6] || (_cache[6] = 
				() => {
					images.value[current.value - 1].isLoading = false;
					images.value[current.value - 1].isError = true;
				}
			),
          onClick: _cache[7] || (_cache[7] = () => current.value++),
          class: _normalizeClass(
				(images.value[current.value - 1].isLoading ? 'w-0 h-0' : '!h-full !w-full') +
				' object-cover rounded-md '
			),
          loading: "lazy",
          referrerPolicy: "no-referrer"
        }, null, 42, _hoisted_6))
      : _createCommentVNode("", true)
  ]))
}
}

})